import { LineChart as LineChartIcon, Package } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { InventoryItem } from "@/hooks/inventory/useInventoryData";;

export function InventoryModal({
  isOpen,
  setIsOpen,
  editingItem,
  setEditingItem,
  categoryVal,
  setCategoryVal,
  uniqueCategories,
  saveItem,
  activeCompanyId,
  modalTab,
  setModalTab,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  editingItem: InventoryItem | null;
  setEditingItem: (val: InventoryItem | null) => void;
  categoryVal: string;
  setCategoryVal: (val: string) => void;
  uniqueCategories: string[];
  saveItem: any;
  activeCompanyId: string | null;
  modalTab: "details" | "history";
  setModalTab: (val: "details" | "history") => void;
}) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setIsOpen(false);
          setEditingItem(null);
          setCategoryVal("");
          setModalTab("details");
        }
      }}
    >
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700 text-slate-100 p-0 overflow-hidden">
        {editingItem && (
          <div className="flex border-b border-slate-800 bg-slate-950/50">
            <button
              className={`flex-1 py-3 text-sm font-medium transition-colors border-b-2 ${
                modalTab === "details"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
              onClick={() => setModalTab("details")}
            >
              <div className="flex items-center justify-center gap-2">
                <Package size={16} /> Detalhes do Insumo
              </div>
            </button>
            <button
              className={`flex-1 py-3 text-sm font-medium transition-colors border-b-2 ${
                modalTab === "history"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
              onClick={() => setModalTab("history")}
            >
              <div className="flex items-center justify-center gap-2">
                <LineChartIcon size={16} /> Histórico de Custos
              </div>
            </button>
          </div>
        )}

        <div className="p-6">
          {!editingItem && (
            <DialogHeader className="mb-4">
              <DialogTitle className="text-xl flex items-center gap-2">
                <Package size={20} className="text-blue-500" /> Novo Insumo
              </DialogTitle>
            </DialogHeader>
          )}

          {modalTab === "details" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                saveItem.mutate(
                  {
                    id: editingItem?.id,
                    company_id: activeCompanyId,
                    name: fd.get("name"),
                    category: categoryVal,
                    qty: Number(fd.get("qty")),
                    min_qty: Number(fd.get("min_qty")),
                    max_qty: Number(fd.get("max_qty")),
                    unit: fd.get("unit"),
                    cost_price: Number(fd.get("cost_price")),
                  },
                  {
                    onSuccess: () => {
                      setIsOpen(false);
                      setEditingItem(null);
                      setCategoryVal("");
                      setModalTab("details");
                    },
                  },
                );
              }}
              className="space-y-4"
            >
              <div className="grid gap-2">
                <Label className="text-slate-300">Nome do Insumo *</Label>
                <Input
                  name="name"
                  defaultValue={editingItem?.name}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Ex: Cera Cristalizadora"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-slate-300">Categoria *</Label>
                  <Input
                    name="category"
                    value={categoryVal}
                    onChange={(e) => setCategoryVal(e.target.value)}
                    required
                    autoComplete="off"
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Ex: Acabamento"
                  />
                  {uniqueCategories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {uniqueCategories.map((c) => (
                        <span
                          key={c}
                          onClick={() => setCategoryVal(c)}
                          className="text-[10px] bg-slate-800 border border-slate-700 text-slate-400 px-2 py-0.5 rounded-full cursor-pointer hover:bg-slate-700 hover:text-white transition-colors uppercase tracking-wider"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label className="text-slate-300">Unidade *</Label>
                  <select
                    name="unit"
                    defaultValue={editingItem?.unit || "%"}
                    required
                    className="bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="%">% (Percentual de Uso)</option>
                    <option value="UN">UN (Unidade)</option>
                    <option value="L">L (Litros)</option>
                    <option value="ml">ml (Mililitros)</option>
                    <option value="Kg">Kg (Quilogramas)</option>
                    <option value="g">g (Gramas)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-2">
                  <Label className="text-slate-300 text-xs">Qtd Atual *</Label>
                  <Input
                    type="number"
                    name="qty"
                    defaultValue={editingItem?.qty ?? 100}
                    required
                    min="0"
                    className="bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-slate-300 text-xs">Qtd Mínima *</Label>
                  <Input
                    type="number"
                    name="min_qty"
                    defaultValue={editingItem?.min_qty ?? 20}
                    required
                    min="0"
                    className="bg-slate-800 border-slate-700 text-white font-mono text-red-300"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-slate-300 text-xs">Qtd Máxima *</Label>
                  <Input
                    type="number"
                    name="max_qty"
                    defaultValue={editingItem?.max_qty ?? 100}
                    required
                    min="1"
                    className="bg-slate-800 border-slate-700 text-white font-mono text-emerald-300"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label className="text-slate-300">Preço de Custo Atual *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                    R$
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    name="cost_price"
                    defaultValue={editingItem?.cost_price ?? 0}
                    required
                    min="0"
                    className="pl-9 bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-2 border-t border-slate-800">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setIsOpen(false);
                    setEditingItem(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saveItem.isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saveItem.isPending ? "Salvando..." : "Salvar Insumo"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">
                  Evolução de Custo:{" "}
                  <strong className="text-slate-200">
                    {editingItem?.name}
                  </strong>
                </span>
                <span className="text-slate-500 text-xs">
                  Últimos registros
                </span>
              </div>

              {editingItem?.cost_history &&
              editingItem.cost_history.length > 1 ? (
                <div className="h-[250px] w-full mt-4 bg-slate-950/30 rounded-xl p-4 border border-slate-800/50">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={editingItem.cost_history}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#1e293b"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="date"
                        stroke="#64748b"
                        fontSize={10}
                        tickFormatter={(val) =>
                          new Date(val).toLocaleDateString("pt-BR", {
                            month: "short",
                            day: "numeric",
                          })
                        }
                        tickMargin={10}
                      />
                      <YAxis
                        stroke="#64748b"
                        fontSize={10}
                        tickFormatter={(val) => `R$${val}`}
                        tickMargin={10}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          borderColor: "#334155",
                          borderRadius: "8px",
                          color: "#f8fafc",
                        }}
                        labelFormatter={(val: any) =>
                          val ? new Date(val).toLocaleDateString("pt-BR") : ""
                        }
                        formatter={(value: any) => [
                          `R$ ${Number(value || 0).toFixed(2)}`,
                          "Custo",
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey="price"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ fill: "#3b82f6", r: 4 }}
                        activeDot={{ r: 6, fill: "#60a5fa" }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500 bg-slate-800/30 rounded-xl border border-slate-700/50">
                  <LineChartIcon
                    className="mx-auto mb-2 opacity-50"
                    size={32}
                  />
                  Histórico insuficiente. Altere o preço de custo para começar a
                  rastrear a inflação deste item.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
